// Settings › Voice, on this browser: which keys talk, one to three ways per action (the chords' rules
// are in voiceGesture.ts). Keys are a device's own choice, so they stay in localStorage, as "On this
// computer" does in notify.ts, and are read each time a key goes down. The first version kept one
// chord per action under `cmx.voice.keys`; it is read once and moved over.

import { DEFAULT_BINDINGS, MAX_BINDINGS, sameBinding, usable, type Binding, type Mod, type Shortcut, type VoiceBindings } from './voiceGesture';

const KEY = 'cmx.voice.bindings';
const OLD_KEY = 'cmx.voice.keys';
const MODS: Mod[] = ['Control', 'Alt', 'Meta', 'Shift'];
const listeners = new Set<() => void>();
/** What's been chosen, for this visit even when the browser won't keep it (a private window). */
let chosen: Partial<VoiceBindings> | null = null;
let current: VoiceBindings | null = null;

const chord = (v: unknown): Shortcut | null => {
  if (!v || typeof v !== 'object') return null;
  const { code, alt, ctrl, meta, shift } = v as Record<string, unknown>;
  if (typeof code !== 'string') return null;
  const s: Shortcut = { code, alt: alt === true, ctrl: ctrl === true, meta: meta === true, shift: shift === true };
  return usable(s) ? s : null;
};

const binding = (v: unknown): Binding | null => {
  if (!v || typeof v !== 'object') return null;
  const { kind, mod } = v as Record<string, unknown>;
  if (kind === 'hold' || kind === 'double') return MODS.includes(mod as Mod) ? { kind, mod: mod as Mod } : null;
  const s = chord(v);
  return s && { kind: 'chord', ...s };
};

const list = (v: unknown): Binding[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out: Binding[] = [];
  for (const b of v.map(binding)) if (b && !out.some((o) => sameBinding(o, b)) && out.length < MAX_BINDINGS) out.push(b);
  return out.length ? out : undefined;
};

function load(): Partial<VoiceBindings> {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved) {
      const v = JSON.parse(saved) as Record<string, unknown>;
      return { push: list(v.push), handsFree: list(v.handsFree) };
    }
    const old = JSON.parse(localStorage.getItem(OLD_KEY) ?? '{}') as Record<string, unknown>;
    const push = chord(old.push);
    const handsFree = chord(old.handsFree);
    return { push: push ? [{ kind: 'chord', ...push }] : undefined, handsFree: handsFree ? [{ kind: 'chord', ...handsFree }] : undefined };
  } catch {
    return {};
  }
}

let recording = 0;
/** A shortcut field in Settings is recording: it owns the keyboard, so the app's own shortcuts and the voice keys stand down. */
export const recordingKeys = () => recording > 0;
/** Start recording; the function it returns ends it (safe to call twice). */
export function startRecordingKeys(): () => void {
  recording++;
  let on = true;
  return () => {
    if (on) recording--;
    on = false;
  };
}

/** The bindings now: what was chosen, the defaults for what wasn't. */
export function voiceBindings(): VoiceBindings {
  chosen ??= load();
  current ??= { push: chosen.push ?? DEFAULT_BINDINGS.push, handsFree: chosen.handsFree ?? DEFAULT_BINDINGS.handsFree };
  return current;
}

const isDefault = (k: keyof VoiceBindings, l: Binding[]) => l.length === DEFAULT_BINDINGS[k].length && l.every((b, i) => sameBinding(b, DEFAULT_BINDINGS[k][i]));

/** Set one action's list; `null` (or the default list) puts the default back. */
export function setVoiceBindings(k: keyof VoiceBindings, l: Binding[] | null) {
  const next: Partial<VoiceBindings> = { ...(chosen ?? load()) };
  if (l && l.length && !isDefault(k, l)) next[k] = l.slice(0, MAX_BINDINGS);
  else delete next[k];
  chosen = next;
  current = null;
  try {
    localStorage.removeItem(OLD_KEY);
    if (next.push || next.handsFree) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    /* private window: for this visit only */
  }
  listeners.forEach((f) => f());
}

/** Both actions back to the defaults. */
export function resetVoiceBindings() {
  setVoiceBindings('push', null);
  setVoiceBindings('handsFree', null);
}

/** Anything chosen differs from the defaults. */
export const voiceBindingsChanged = () => {
  const b = voiceBindings();
  return !isDefault('push', b.push) || !isDefault('handsFree', b.handsFree);
};

export function onVoiceShortcuts(f: () => void) {
  listeners.add(f);
  return () => void listeners.delete(f);
}
